/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import * as Sentry from '@sentry/react';
import {
  action,
  computed,
  Computed,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  thunk,
} from 'easy-peasy';

import { User, UserOrg, AppUser } from '../types';
import { BASE_ONBOARDING_API, BASE_USER_API } from '../utils/constants';
import { fetchWithAuthV2 } from '../utils/requests';

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

/* 
  eulaLink will always have the latest version ouster present to user during onboarding
  userEulaLink is what user actually accepted. It might have different version than eulaLink
*/

export type State = {
  isAuthenticated: boolean;
  user?: User;
  appUser?: AppUser;
  userOwnOrgs: UserOrg[];
  userOrgs: UserOrg[];
  curUserOrg?: UserOrg;
  eulaLink: string; // this link what user see during onboarding
  eulaPdfLink: string; // this pdf version of link what user see during onboarding
  userEulaLink: string; // this link is what user accepted during onboarding
};

export const defaultState: State = {
  isAuthenticated: false,
  userOrgs: [],
  userOwnOrgs: [],
  eulaLink: '',
  eulaPdfLink: '',
  userEulaLink: '',
};

export interface UserStateModel extends State {
  setUser: EzPzAction<UserStateModel, User | undefined>;
  setIsAuthenticated: EzPzAction<UserStateModel, boolean>;
  setUserOrgs: EzPzAction<UserStateModel, UserOrg[]>;
  setUserOwnOrgs: EzPzAction<UserStateModel, UserOrg[]>;
  setAppUser: EzPzAction<UserStateModel, AppUser>;
  setCurUserOrg: EzPzAction<UserStateModel, number>;
  setEulaLink: EzPzAction<UserStateModel, string>;
  setEulaPdfLink: EzPzAction<UserStateModel, string>;
  setUserEulaLink: EzPzAction<UserStateModel, string>;
  fetchUserOrgs: EzPzThunk<UserStateModel, string>;
  fetchUserOwnOrgs: EzPzThunk<UserStateModel, string>;
  fetchUserRoleByIdentity: EzPzThunk<UserStateModel, string>;
  fetchEulaLink: EzPzThunk<UserStateModel>;
  fetchUserEulaStatus: EzPzThunk<UserStateModel, string>;
  fetchUserEulaLink: EzPzThunk<UserStateModel, string>;
  fetchUserAccountStatus: EzPzThunk<UserStateModel, string>;
  registerUser: EzPzThunk<UserStateModel>;
  updateUserEulaStatus: EzPzThunk<
    UserStateModel,
    { status: boolean; identity_id: string }
  >;
  updateUserName: EzPzThunk<
    UserStateModel,
    { name: string; identity_id: string }
  >;
  eulaAcceptance: EzPzThunk<
    UserStateModel,
    {
      status: boolean;
      identity_id: string;
      version: string;
      country: string;
      state: string;
    }
  >;
  signUpUserWithEmail: EzPzThunk<
    UserStateModel,
    { email: string; password: string }
  >;
  validateArea: EzPzThunk<UserStateModel, { country: string; state: string }>;
  validateInvitation: EzPzThunk<
    UserStateModel,
    { email: string; invitation_uuid: string; org_uuid: string }
  >;
  forgotPassword: EzPzThunk<UserStateModel, { email: string }>;
  resetPassword: EzPzThunk<
    UserStateModel,
    { email: string; code: string; new_password: string }
  >;
  updateUserMarketingPreference: EzPzThunk<
    UserStateModel,
    { identity_id: string; opt_out: boolean }
  >;
  hasMultiOrgs: Computed<UserStateModel, boolean>;
  isOusterUser: Computed<UserStateModel, boolean>;
}

export const model: UserStateModel = {
  ...defaultState,
  setUser: action((state, user) => {
    state.user = user;
  }),
  setIsAuthenticated: action((state, authState) => {
    state.isAuthenticated = authState;
  }),
  setUserOrgs: action((state, userOrgs) => {
    state.userOrgs = userOrgs;

    if (userOrgs.length === 0) return;

    const savedOrgId = localStorage.getItem('lastOrgId');
    const savedOrg = savedOrgId
      ? userOrgs.find((org) => org.id === parseInt(savedOrgId, 10))
      : undefined;
    // Provisioned org is the default when no last-visited org is saved, so the
    // user lands in the provisioning flow rather than an inaccessible org.
    const provisionedOrg = userOrgs.find((org) => org.is_provisioned);
    const hasOusterOrg = userOrgs.some((org) => org.id === 1);
    if (hasOusterOrg) {
      // Ouster user priority: last visited org → provisioned org →  first org
      state.curUserOrg = savedOrg ?? provisionedOrg ?? userOrgs[0];
    } else {
      // Non ouster user priority: provisioned org → last visited org → first org

      state.curUserOrg = provisionedOrg ?? savedOrg ?? userOrgs[0];
    }
    localStorage.setItem('lastOrgId', state.curUserOrg.id.toString());
  }),
  setUserOwnOrgs: action((state, userOrgs) => {
    state.userOwnOrgs = userOrgs;
  }),
  setAppUser: action((state, appUser) => {
    state.appUser = appUser;
  }),
  setCurUserOrg: action((state, newOrgId) => {
    localStorage.setItem('lastOrgId', newOrgId.toString());
    state.curUserOrg = state.userOrgs.find((org) => org.id === newOrgId);
  }),
  setEulaLink: action((state, link) => {
    state.eulaLink = link;
  }),
  setEulaPdfLink: action((state, link) => {
    state.eulaPdfLink = link;
  }),
  setUserEulaLink: action((state, link) => {
    state.userEulaLink = link;
  }),
  hasMultiOrgs: computed(
    (state) => !!(state.userOrgs && state.userOrgs.length > 1)
  ),
  isOusterUser: computed((state) => !!(state.curUserOrg?.id === 1)),
  fetchUserOrgs: thunk(async (actions, identityId) => {
    // get all the organization that user belong
    const path = `${BASE_USER_API}/${identityId}/organizations/`;
    try {
      const userOrgs = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage:
          'failed to get user joined organization by identity',
      });
      actions.setUserOrgs(userOrgs);
    } catch (err) {
      console.error('Failed to fetchUserOrgs: ', err);
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchUserOrgs' },
        extra: { identityId },
      });
    }
  }),
  fetchUserOwnOrgs: thunk(async (actions, identityId) => {
    // get the organization that user created, if none, {} will return
    const path = `${BASE_USER_API}/${identityId}/organization/`;
    try {
      const organizations = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage:
          'Failed to get user created organizations by identity',
      });
      actions.setUserOwnOrgs(organizations);
    } catch (err) {
      console.error('Failed to fetch user own organizations: ', err);
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchUserOwnOrgs' },
        extra: { identityId },
      });
    }
  }),
  fetchUserRoleByIdentity: thunk(async (actions, identityId) => {
    const path = `${BASE_USER_API}/${identityId}`;

    try {
      const userData = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get user by identity',
      });
      actions.setAppUser(userData);
    } catch (err) {
      console.error('Failed to fetch user role: ', err);
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchUserRoleByIdentity' },
        extra: { identityId },
      });
      throw err;
    }
  }),
  fetchUserEulaStatus: thunk(async (actions, identityId) => {
    const path = `${BASE_USER_API}/${identityId}/eula_acceptance`;
    try {
      const userEulaStatus = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get user EULA status',
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!userEulaStatus.status) {
        actions.updateUserEulaStatus({ status: true, identity_id: identityId });
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchUserEulaStatus' },
        extra: { identityId },
      });
    }
  }),
  fetchUserEulaLink: thunk(async (actions, identityId) => {
    const path = `${BASE_USER_API}/${identityId}/user_accepted_eula_link`;

    try {
      const { EULA_link: eulaLink } = await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get user accepted EULA link',
      });
      actions.setUserEulaLink(eulaLink);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchUserEulaLink' },
        extra: { identityId },
      });
    }
  }),
  fetchUserAccountStatus: thunk(async (actions, email) => {
    const path = `${BASE_ONBOARDING_API}/get_status`;

    try {
      return (await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to get user account status',
        body: JSON.stringify({ email }),
      })) as { disabled: boolean };
    } catch (err) {
      console.error('Failed to fetchUserAccountStatus: ', err);
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchUserAccountStatus' },
        extra: { email },
      });
      throw err;
    }
  }),
  fetchEulaLink: thunk(async (actions) => {
    const path = `${BASE_ONBOARDING_API}/eula_file_link`;

    try {
      const data = (await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage: 'Failed to get EULA link',
      })) as { EULA_link: string; EULA_pdf_link: string; version: string };
      actions.setEulaLink(data.EULA_link);
      actions.setEulaPdfLink(data.EULA_pdf_link);
      sessionStorage.setItem('eula-version', data.version);
    } catch (err) {
      console.error('Failed to fetchEulaLink: ', err);
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'fetchEulaLink' },
      });
    }
  }),
  updateUserName: thunk(
    async (actions, { name, identity_id }, { getState }) => {
      const path = `${BASE_USER_API}/${identity_id}`;

      try {
        await fetchWithAuthV2(path, {
          method: 'PATCH',
          fallbackErrorMessage: 'Failed to update user name',
          body: JSON.stringify({ name }),
        });
        const { appUser } = getState();
        if (appUser) actions.setAppUser({ ...appUser, name });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: 'UserStateModel', thunk: 'updateUserName' },
          extra: { name, identity_id },
        });
        throw err;
      }
    }
  ),
  registerUser: thunk(async () => {
    const path = `${BASE_USER_API}/registration`;

    try {
      const response = await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to register user',
      });
      const { id } = response;

      localStorage.setItem('lastOrgId', String(id));
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'registerUser' },
      });
      throw new Error('Failed to register user');
    }
  }),
  eulaAcceptance: thunk(
    async (actions, { status, identity_id, version, country, state }) => {
      const path = `${BASE_USER_API}/${identity_id}/eula_acceptance`;

      try {
        await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage: 'Failed to create user eula acceptance',
          body: JSON.stringify({ status, version, country, state }),
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { section: 'UserStateModel', thunk: 'eulaAcceptance' },
          extra: { status, identity_id, version, country, state },
        });
        throw err;
      }
    }
  ),
  updateUserEulaStatus: thunk(async (actions, { status, identity_id }) => {
    const path = `${BASE_USER_API}/${identity_id}/eula_acceptance`;

    try {
      await fetchWithAuthV2(path, {
        method: 'PATCH',
        fallbackErrorMessage: 'Failed to update user eula acceptance',
        body: JSON.stringify({ status }),
      });
    } catch (err) {
      console.error('Failed to updateUserEulaStatus: ', err);
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'updateUserEulaStatus' },
        extra: { status, identity_id },
      });
    }
  }),
  updateUserMarketingPreference: thunk(
    async (actions, { identity_id, opt_out }, { getState }) => {
      const path = `${BASE_USER_API}/${identity_id}/eula_opt_out`;

      try {
        await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage: 'Failed to update marketing preferences',
          body: JSON.stringify({ opt_out }),
        });
        const { appUser } = getState();
        if (appUser) {
          actions.setAppUser({ ...appUser, email_opt_out: opt_out });
        }
      } catch (err) {
        console.error('Failed to update marketing preferences: ', err);
        Sentry.captureException(err, {
          tags: {
            section: 'UserStateModel',
            thunk: 'updateUserMarketingPreference',
          },
          extra: { identity_id, opt_out },
        });
        throw err;
      }
    }
  ),
  signUpUserWithEmail: thunk(async (_, { email, password }) => {
    const path = `${BASE_ONBOARDING_API}/`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to sign up user',
        body: JSON.stringify({ email, password }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'signUpUserWithEmail' },
        extra: { email },
      });
      throw err;
    }
  }),
  validateInvitation: thunk(async (_, { email, invitation_uuid, org_uuid }) => {
    const path = `${BASE_ONBOARDING_API}/validate_invitation`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to sign up user',
        body: JSON.stringify({
          email,
          invitation_uuid,
          organization_uuid: org_uuid,
        }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'validateInvitation' },
        extra: { email, invitation_uuid, org_uuid },
      });
      throw err;
    }
  }),
  validateArea: thunk(async (_, { country, state }) => {
    const path = `${BASE_ONBOARDING_API}/area_validation`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to validate area',
        body: JSON.stringify({ country, state }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'validateArea' },
        extra: { country, state },
      });
      throw err;
    }
  }),
  forgotPassword: thunk(async (_, { email }) => {
    const path = `${BASE_ONBOARDING_API}/forgot_password`;
    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to trigger forgot password process',
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'forgotPassword' },
        extra: { email },
      });
      throw err;
    }
  }),
  resetPassword: thunk(async (_, { email, code, new_password }) => {
    const path = `${BASE_ONBOARDING_API}/reset_password`;
    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to trigger reset password process',
        body: JSON.stringify({ email, code, new_password }),
      });
    } catch (err) {
      // we don't send user's new password to sentry
      Sentry.captureException(err, {
        tags: { section: 'UserStateModel', thunk: 'resetPassword' },
        extra: { email, code },
      });
      throw err;
    }
  }),
};
